import {
  AUTH_PRESERVED_LOCAL_STORAGE_KEYS,
  EMITTER_CONSTANTS,
  ERROR_ACTION,
  LIST_ROLES_CALL_CENTER,
  URL_CALLCENTER_PORTAL,
  URL_ERP_PORTAL,
} from '@/shared/config';
import { logError } from '@/shared/lib/apiHelpers';
import { useUserStore } from '@/entities/user';
import { auth } from '@/shared/lib';
import emitter from '@/shared/lib/eventEmitter';
import socketio from '@/shared/lib/socketio';
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import _ from 'lodash';

/**
 * Signals other open portal tabs that this one has logged out. A logout-event entry carrying the redirect target and a timestamp is written to localStorage, which sibling tabs observe through the storage event.
 *
 * @param redirectUrl - Where sibling tabs should navigate after logging out.
 * @returns Nothing.
 */
const emitLogoutEvent = (redirectUrl = URL_CALLCENTER_PORTAL) => {
  localStorage.setItem(
    'logout-event',
    JSON.stringify({
      redirectUrl,
      ts: Date.now(),
    }),
  );
};

/**
 * Expires every cookie visible to the portal as part of signing a user out. Each entry from document.cookie is rewritten with an expiry of the current instant, scoped to the root path and the current hostname, so the browser drops it.
 *
 * @returns Nothing.
 */
const clearCookies = () => {
  //* clear bằng cách set expire thành hiện tại để browser clear
  _.forEach(document.cookie.split(';'), (c) => {
    document.cookie = c
      .replace(/^ +/, '')
      .replace(
        /=.*/,
        `=; expires=${new Date().toUTCString()}; path=/; domain=${window.location.hostname}`,
      );
  });
};

const communicateToErpPortal = ({
  action,
  key,
  value,
}: {
  action?: any;
  key?: any;
  value?: any;
}) => {
  const iframe = document.getElementById('iframe-id') as HTMLIFrameElement;
  if (iframe) {
    iframe.contentWindow?.postMessage(
      {
        action,
        key,
        value,
      },
      '*',
    );
  }
};

type SessionResult = { isSuccess: boolean; error?: string };

/**
 * Renews an expired Cognito id token from the session's refresh token. The callback-style refresh is wrapped in a promise that always resolves, reporting the failure message rather than rejecting.
 *
 * @param authUser - Cognito user whose session is being refreshed.
 * @param session - Current session supplying the refresh token.
 * @returns A promise resolving to a result carrying isSuccess and, on failure, error.
 */
const refreshSession = (authUser: any, session: any): Promise<SessionResult> =>
  new Promise((resolve) => {
    authUser.refreshSession(session.refreshToken, (error: any) => {
      if (error) {
        resolve({
          isSuccess: false,
          error: error.message,
        });
      } else {
        resolve({
          isSuccess: true,
        });
      }
    });
  });

/**
 * Makes sure a Cognito session handed over from another portal is still usable. An id token whose expiry has passed is refreshed, otherwise the current session is simply fetched to confirm it, and any throw is reported as a failure result rather than propagated.
 *
 * @param authUser - Cognito user the session belongs to.
 * @param session - Session whose id token expiry is checked.
 * @returns A promise resolving to a result carrying isSuccess and, on failure, error.
 */
const validateSession = async (authUser: any, session: any): Promise<SessionResult> => {
  try {
    const idTokenExpire = session.getIdToken().getExpiration();
    const currentTimeSeconds = Math.round(+new Date() / 1000);
    if (idTokenExpire <= currentTimeSeconds) {
      const refreshResult = await refreshSession(authUser, session);
      return refreshResult;
    }
    await Auth.currentSession();
    return {
      isSuccess: true,
    };
  } catch (error: any) {
    return {
      isSuccess: false,
      error: error.message,
    };
  }
};

/**
 * Adopts tokens shared by the ERP portal as the browser's active Cognito session. A session is assembled from the shared id, access and refresh tokens, attached to a Cognito user created for the shared email, then validated and refreshed if needed, with any throw reported as a failure result.
 *
 * @param shareData - Cross-portal payload carrying email plus the id, access and refresh tokens.
 * @returns A promise resolving to a result carrying isSuccess and, on failure, error.
 */
const createNewCognitoUser = async (shareData: any): Promise<SessionResult> => {
  try {
    const session = new CognitoUserSession({
      IdToken: new CognitoIdToken({ IdToken: shareData.idToken }),
      AccessToken: new CognitoAccessToken({
        AccessToken: shareData.accessToken,
      }),
      RefreshToken: new CognitoRefreshToken({
        RefreshToken: shareData.refreshToken,
      }),
    });
    // AuthClass#createCognitoUser is typed private by @types/aws-amplify, but this
    // is the documented workaround for share-data cross-portal sign-in — pre-existing.
    const authUser = (Auth as any).createCognitoUser(shareData.email);
    authUser.setSignInUserSession(session);
    const result = await validateSession(authUser, session);
    return result;
  } catch (error: any) {
    return {
      isSuccess: false,
      error: error.message,
    };
  }
};

/**
 * Tears down the browser's existing Cognito session before a different user is signed in. Cookies are expired, localStorage is cleared outright and Cognito sign-out is awaited, with any throw logged rather than propagated.
 *
 * @returns A promise resolving once sign-out has been attempted.
 */
const handleSignOutCurrentCognitoUser = async () => {
  try {
    clearCookies();
    localStorage.clear();
    await Auth.signOut();
  } catch (error) {
    console.log('[authenticationGate]-handleSignOutCurrentCognitoUser ~ error:', error);
  }
};

/**
 * Confirms the Cognito session already in the browser belongs to the user the portal was handed. The current authenticated user is fetched and its email compared with the expected one, with a mismatch or a thrown lookup both reported as a failure.
 *
 * @param email - Email the active session is expected to belong to.
 * @returns A promise resolving to true when the active Cognito user matches the email.
 */
const handleCheckCurrentCognitoUser = async (email: string) => {
  try {
    const currentCognitoUser = await Auth.currentAuthenticatedUser();
    const currentEmail = currentCognitoUser?.attributes?.email;
    if (currentEmail !== email) {
      return false;
    }
    return true;
  } catch (error) {
    console.error('Failed to check current Cognito user: ', error);
    return false;
  }
};

//* Nếu ko nhận đc share data check options chrome://flags -> experimental third-party storage partitioning
// https://developer.chrome.com/docs/privacy-sandbox/first-party-sets-integration/#requesting-access-to-storage-from-an-embedded-cross-origin-iframe
// https://developer.chrome.com/en/docs/privacy-sandbox/storage-partitioning/

/**
 * Wipes localStorage while preserving a named allowlist, so cached beat status survives a sign-out. Allowlist entries ending in an asterisk become prefix regexes, and every stored key that matches neither a literal nor a pattern is removed.
 *
 * @param keysToKeep - Literal keys or asterisk-suffixed patterns to preserve.
 * @returns Nothing.
 */
function clearLocalStorageExcept(keysToKeep: string[]) {
  const keysToKeepSet = new Set(
    keysToKeep.map((key) => {
      if (key.endsWith('*')) {
        return new RegExp(`^${key.replace('*', '.*')}$`);
      }
      return key;
    }),
  );

  // Iterate through all keys in localStorage
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    // If the key is not in the keysToKeep list, remove it
    if (
      ![...keysToKeepSet].some((pattern) => {
        if (pattern instanceof RegExp) {
          return pattern.test(key as string);
        }
        return key === pattern;
      })
    ) {
      localStorage.removeItem(key as string);
    }
  }
}

/**
 * Clears the portal's localStorage on sign-out without losing the download state it must keep. The wipe runs against the shared list of auth-preserved keys.
 *
 * @returns Nothing.
 */
const clearAuthLocalStorage = () => {
  clearLocalStorageExcept(AUTH_PRESERVED_LOCAL_STORAGE_KEYS);
};

/**
 * Handles the cross-window messages the ERP portal uses to hand this portal a signed-in session. Messages from another origin are ignored; a share payload with a username access token clears auth localStorage, stores the token and emits the token-update event, otherwise a missing access token or a mismatched or unadoptable Cognito user forces a logout redirect, and a good payload saves the tokens, connects the socket and fetches the current user; a sign-out message simply emits the logout request.
 *
 * @param event - Cross-window message carrying the action and share payload.
 * @returns A promise resolving once the message has been handled.
 */
const messageHandler = async (event: MessageEvent) => {
  // *: For testing: comment this code block
  const { origin } = event;
  if (origin !== URL_ERP_PORTAL) {
    return;
  }

  const { action, value } = event.data;
  console.log('[authenticationGate]-MESSAGE', action, event);
  switch (action) {
    case 'returnDataAiReport': {
      try {
        const { shareData } = value;
        console.log(
          '🚀 ~ file: authenticationGate.h.js:134 ~ messageHandler ~ shareData:',
          shareData,
        );

        if (shareData?.usernameAccessToken) {
          /*
            Clear localStorage để tránh trường hợp người dùng đăng nhập bằng sso --> qua airp
            --> lưu thông tin current session vào localstorage -->
            tắt tab airp --> đăng xuất sso --> đăng nhập lại bằng username --> quay lại airp: lúc này thông tin được lưu ở bước đó trước sẽ không bị xoá khỏi localstorage
          */

          // localStorage.clear();
          // not clear all local storage except keyBeatDataStatus
          clearAuthLocalStorage();

          const { usernameAccessToken } = shareData;
          auth.setToken(usernameAccessToken);
          emitter.emit(EMITTER_CONSTANTS.UPDATE_TOKEN_LOCAL_STORAGE);
        } else {
          if (_.isNil(shareData?.accessToken)) {
            await handleSignOutCurrentCognitoUser();
            emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
            return;
          }

          const isValidCurrentUser = await handleCheckCurrentCognitoUser(shareData?.email);

          if (!isValidCurrentUser) {
            await handleSignOutCurrentCognitoUser();
            const result = await createNewCognitoUser(shareData);
            if (!result?.isSuccess) {
              emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
              return;
            }
          }

          const { accessToken, idToken, refreshToken, email } = shareData;
          auth.saveShareData({
            accessToken,
            idToken,
            refreshToken,
            email,
          });

          await socketio.connectToServer();
          useUserStore.getState().fetchMe();
        }
      } catch (error) {
        logError('Failed to create Cognito user: ', error);
        emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
      }
      break;
    }
    case 'signOutUsernameCurrentCallcenterPortal': {
      console.log('[authenticationGate]-signOutUsernameCurrentCallcenterPortal');
      emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
      break;
    }
    default: {
      break;
    }
  }
};

/**
 * Decides whether a user's roles grant access to the call-center portal. An empty or non-array input is rejected outright, otherwise the roles are tested for any overlap with the call-center role list.
 *
 * @param roles - Role names claimed by the signed-in user.
 * @returns True when at least one role is a call-center role, false otherwise.
 */
const handleCheckRoles = (roles: string[]) => {
  if (_.isEmpty(roles) || !_.isArray(roles)) {
    return false;
  }

  return _.some(LIST_ROLES_CALL_CENTER, (roleCallcenter) => _.includes(roles, roleCallcenter));
};

export { clearCookies, communicateToErpPortal, emitLogoutEvent, handleCheckRoles, messageHandler };
